import { z } from "zod";
import type { OptionType } from "../../../../../types/types";
import {
  AttributeSchema,
  DocumentAttributeValue,
} from "../../../../../types/types.PIM";
import {
  AddressValue,
  transformAddress,
} from "../../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";
import { LinkAttributeValueSchema } from "../../../../../util/zod.util";

export type DocumentCollectionFormValue = Pick<
  DocumentAttributeValue,
  "asset_id"
> & { value_id?: string };

export const SUPPORTED_COLLECTION_INPUT_TYPES = new Set([
  "form_field",
  "single_select",
  "product_reference",
  "object_reference",
  "multi_select",
  "numeric",
  "multiline_entry",
  "checkbox",
  "date",
  "toggle",
  "link",
  "user",
  "address",
  "document",
]);

export const isDocumentFormValue = (
  val: unknown
): val is DocumentCollectionFormValue =>
  typeof val === "object" &&
  val !== null &&
  !Array.isArray(val) &&
  "asset_id" in val &&
  typeof (val as { asset_id: unknown }).asset_id === "string" &&
  (val as { asset_id: string }).asset_id.length > 0;

export const isAddressFormValue = (val: unknown): val is AddressValue =>
  typeof val === "object" &&
  val !== null &&
  !Array.isArray(val) &&
  ("address1" in val ||
    "company_name" in val ||
    "country" in val ||
    "country_code" in val);

export const handleCollectionFormValue = (val: unknown) => {
  if (typeof val === "string") {
    return val ? [val] : null;
  } else if (val === undefined || val === null) {
    return null;
  } else if (typeof val === "boolean") {
    return [val];
  } else if (Array.isArray(val)) {
    if (val.length > 0 && val.every(isDocumentFormValue)) {
      return val.map(({ asset_id }) => ({ asset_id }));
    }
    return val
      .map((v) =>
        typeof v === "string"
          ? v
          : typeof v === "object" && v !== null && "name" in v
          ? (v as { id?: string; name?: string }).id ??
            (v as { name?: string }).name
          : (v as OptionType).value
      )
      .filter((v) => v) as string[] | null;
  } else if (typeof val === "object") {
    const parsedLink = LinkAttributeValueSchema.safeParse(val);
    const parsedOptionType = z
      .object({
        value: z.union([z.string(), z.boolean()]),
        label: z.string(),
      })
      .safeParse(val);

    if (parsedLink.success) {
      return parsedLink.data.url ? [parsedLink.data] : null;
    }

    if (parsedOptionType.success) {
      return (val as OptionType).value ? [(val as OptionType).value] : null;
    }

    if (isAddressFormValue(val)) {
      const transformed = transformAddress(val);
      return transformed ? [transformed] : null;
    }
  } else if (typeof val === "number") {
    return [val];
  }

  return null;
};

const INPUT_TYPE_TO_DATA_TYPE: Record<string, string> = {
  object_reference: "object_ref",
  product_reference: "product",
  user: "user",
  address: "address",
  document: "document",
  numeric: "numeric",
  date: "date",
  toggle: "boolean",
  checkbox: "boolean",
  single_select: "enum",
  multi_select: "enum",
};

export const getDataTypeForInputType = (inputType: string): string =>
  INPUT_TYPE_TO_DATA_TYPE[inputType] ?? "text";

export const buildFormattedValuesInColumnOrder = (
  columns: AttributeSchema[],
  values: Record<string, unknown>
) =>
  columns.map((col) => ({
    attribute_id: col.id,
    input_type: col.input_type,
    default_values: SUPPORTED_COLLECTION_INPUT_TYPES.has(col.input_type)
      ? handleCollectionFormValue(values[col.id])
      : null,
  }));

/**
 * Returns the display label for an attribute, appending " *" if the attribute is required.
 *
 * @param attr - The attribute schema object
 * @param t - Translation function used to resolve the attribute's display name
 * @returns The localized display name, suffixed with " *" if `attr.is_required` is true
 *
 * @example
 * formatRequiredLabel({ display_name: "color", name: "color", is_required: true }, t)
 * // => "Color *"
 *
 * @example
 * formatRequiredLabel({ display_name: null, name: "size", is_required: false }, t)
 * // => "size"
 */
export const formatRequiredLabel = (
  attr: AttributeSchema,
  t: (key: string | string[]) => string
) => {
  const displayName = attr.display_name ? attr.display_name : attr.name;
  return attr.is_required ? `${displayName} *` : displayName;
};

/**
 * Returns the display label for an attribute, appending " *" if the attribute is mandatory
 * and the asterisk display is enabled.
 *
 * @param attr - The attribute schema object
 * @param t - Translation function used to resolve the attribute's display name
 * @param showMandatoryAsterisk - When true, appends " *" to mandatory attributes
 * @returns The localized display name, suffixed with " *" if both `attr.is_mandatory` and `showMandatoryAsterisk` are true
 *
 * @example
 * formatMandatoryLabel({ display_name: "brand", name: "brand", is_mandatory: true }, t, true)
 * // => "Brand *"
 *
 * @example
 * formatMandatoryLabel({ display_name: "brand", name: "brand", is_mandatory: true }, t, false)
 * // => "Brand"
 */
export const formatMandatoryLabel = (
  attr: AttributeSchema,
  t: (key: string | string[]) => string,
  showMandatoryAsterisk?: boolean
) => {
  const displayName = attr.display_name ? attr.display_name : attr.name;
  return attr.is_mandatory && showMandatoryAsterisk
    ? `${displayName} *`
    : displayName;
};
